import { ProviderPhoto } from "@src/common/ProviderPhoto";
import { APP_ROUTES } from "@src/constants";
import { ABYSS_BREAKPOINTS, MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useProviderListContext } from "@src/context/ProviderListContext";
import { useExclusiveAction } from "@src/hooks/useExclusiveAction";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import type { Provider } from "@src/interfaces/provider.interface";
import type { ProviderPhoto as ProviderPhotoData } from "@src/interfaces/provider-photo.interface";
import { getProviderPhotoMetadata, getProviderPhotoSrc } from "@src/utils/provider-photo-utils";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Badge } from "@uhg-abyss/web/ui/Badge";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { Text } from "@uhg-abyss/web/ui/Text";
import {
  Fragment,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type AvailabilitySelection, ProviderAvailabilityPanel } from "./ProviderAvailabilityPanel";

const IconHolder = styled("div", {
  maxWidth: "132px",
  maxHeight: "132px",
  padding: "23.76px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  backgroundColor: "$app.color.surface.container.primary",
  overflow: "hidden",
  variants: {
    isMobile: {
      true: {
        maxWidth: "80px",
        maxHeight: "80px",
        padding: "14.399px",
      },
      false: {
        maxWidth: "132px",
        maxHeight: "132px",
        padding: "23.76px",
      },
    },
    hasProviderPhoto: {
      true: {
        padding: "0",
        backgroundColor: "transparent",
      },
    },
  },
  compoundVariants: [
    {
      isMobile: false,
      hasProviderPhoto: true,
      css: {
        width: "132px",
        height: "132px",
      },
    },
    {
      isMobile: true,
      hasProviderPhoto: true,
      css: {
        width: "80px",
        height: "80px",
      },
    },
  ],
});

const ProviderCardText = styled(Text, {
  lineHeight: "125% !important",
  variants: {
    isMobile: {
      true: {
        fontSize: "14px !important",
        lineHeight: "18px !important",
      },
      false: {
        lineHeight: "125% !important",
      },
    },
  },
});

const ProviderNameText = styled(ProviderCardText, {
  fontWeight: 700,
  color: "$app.color.text.primary",
});

const ProviderNameLink = styled("button", {
  border: "none",
  backgroundColor: "transparent",
  padding: 0,
  margin: 0,
  textAlign: "left",
  cursor: "pointer",
  color: "inherit",
  font: "inherit",
  textDecoration: "none",
  "&:hover": {
    opacity: 0.9,
  },
});

const ProviderPhotoLink = styled("button", {
  border: "none",
  backgroundColor: "transparent",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  display: "inline-flex",
  alignSelf: "flex-start",
  borderRadius: "8px",
});

const SectionTitleText = styled(ProviderCardText, {
  fontWeight: 500,
  color: "$app.color.text.primary",
});

const ProviderCardBottomSection = styled("div", {
  marginTop: "20px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "flex-end",
  gap: "16px",
  alignSelf: "stretch",
  overflow: "hidden", // prevent child components from leaving parent div
  variants: {
    isMobile: {
      true: {
        marginTop: "14px",
        flexDirection: "column",
        gap: "20px",
      },
      false: {
        marginTop: "20px",
        flexDirection: "row",
        gap: "16px",
      },
    },
  },
});

const SpecialitiesAndModalities = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  flex: "1 0 0",
  width: "fit-content",
  variants: {
    isMobile: {
      true: {
        width: "100%",
      },
      false: {
        width: "fit-content",
      },
    },
  },
});

const CardWrapper = styled("div", {
  gap: "20px",
  variants: {
    isMobile: {
      true: { padding: "4px" },
      false: { padding: "8px" },
    },
  },
});

const ProfileHeader = styled("div", {
  display: "flex",
  gap: "24px",
  whiteSpace: "normal",
  wordWrap: "break-word",
});

const ProfileHeaderInfo = styled("div", {
  display: "flex",
  flexDirection: "column",
  variants: {
    isMobile: {
      true: { gap: "4px" },
      false: { gap: "8px" },
    },
  },
});

const ProviderMetaSection = styled("div", {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  alignItems: "flex-start",
  gap: "8px",
  marginTop: "24px",
  variants: {
    isInlineWithHeader: {
      true: {
        marginTop: "16px",
      },
    },
  },
});

const ModalityBadgesWrapper = styled("div", {
  display: "flex",
  alignItems: "flex-start",
  flexDirection: "row",
  gap: "8px",
  flexWrap: "wrap",
});

const BadgesContainer = styled("div", {
  display: "flex",
  overflow: "hidden",
  width: "100%",
  gap: "8px",
});

const ScheduleButton = styled(Button, {
  whiteSpace: "nowrap",
  variants: {
    isMobile: {
      true: { width: "100%" },
      false: { width: "fit-content" },
    },
  },
});

const AvailabilityLayout = styled("div", {
  display: "grid",
  gridTemplateColumns: "367px 1fr",
  gap: "20px",
  alignItems: "stretch",
  variants: {
    isMobile: {
      true: {
        gridTemplateColumns: "1fr",
      },
    },
    isStacked: {
      true: {
        gridTemplateColumns: "1fr !important",
      },
    },
  },
  [`@media ${MEDIA_QUERIES.consolidatedSchedulingMax}`]: {
    gridTemplateColumns: "minmax(260px, 0.8fr) minmax(0, 1.2fr)",
  },
});

const LeftColumn = styled("div", {
  borderRight: "none",
  paddingRight: "20px",
  variants: {
    isMobile: {
      true: {
        borderRight: "none",
        paddingRight: "0",
      },
    },
  },
});

const RightColumn = styled("div", {
  display: "flex",
  alignItems: "stretch",
  width: "100%",
});

interface ProviderAvailabilityCardData {
  visibleIsoDates: string[];
  availableDateSet: Set<string>;
  slotsByIsoDate: Record<string, import("@src/interfaces/provider.interface").ProviderSlot[]>;
  expandedDateSet: Set<string>;
  isLoadingDates: boolean;
  isLoadingSlots: boolean;
  errorMessage?: string;
  status?: "loading" | "error" | "complete";
  hasNoFilteredAvailability?: boolean;
  selectedSlotKey: string | null;
  isSubmittingSelection: boolean;
  isSchedulingBlocked: boolean;
  canGoPreviousDates: boolean;
  canGoNextDates: boolean;
}

const buildSpecialitiesAndModalities = (
  provider: Provider,
  specialitiesSet: Set<string>,
): string[] => {
  const sortedSpecialitiesAndModalities = [
    ...[...(provider?.conditionPreferences || [])].sort((a, b) => {
      const aInSpecialities = specialitiesSet.has(a.toLowerCase());
      const bInSpecialities = specialitiesSet.has(b.toLowerCase());

      if (aInSpecialities && !bInSpecialities) return -1;
      if (!aInSpecialities && bInSpecialities) return 1;

      return a.toLowerCase().localeCompare(b.toLowerCase());
    }),
    ...[...(provider?.therapyTypes || [])].sort((a, b) => {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    }),
  ];

  return Array.from(new Set(sortedSpecialitiesAndModalities));
};

const calculateVisibleBadgeState = ({
  container,
  badges,
  allLabels,
  isMobile,
}: {
  container: HTMLDivElement;
  badges: Array<HTMLDivElement | null>;
  allLabels: string[];
  isMobile: boolean;
}): { visibleBadges: string[]; remainingCount: number } => {
  const containerWidth = isMobile ? container.offsetWidth * 0.85 : container.offsetWidth * 0.9;
  let totalWidth = 0;
  let maxBadges = 0;

  for (const badge of badges) {
    if (!badge) {
      continue;
    }

    const badgeWidth = badge.getBoundingClientRect().width;
    const gapWidth = 8;
    totalWidth += badgeWidth + gapWidth;

    if (totalWidth <= containerWidth) {
      maxBadges += 1;
    } else {
      break;
    }
  }

  return {
    visibleBadges: allLabels.slice(0, maxBadges),
    remainingCount: allLabels.length - maxBadges,
  };
};

const getClinicTexts = (
  provider: Provider,
): { clinicNameText: string; clinicLocationText: string } => {
  if (provider.departments.length > 1) {
    return {
      clinicNameText: "Multiple locations",
      clinicLocationText: provider.departments
        .map((location) => {
          return location.state;
        })
        .join(", "),
    };
  }

  if (provider.departments.length === 1) {
    return {
      clinicNameText: provider.departments[0].clinic_name,
      clinicLocationText: `${provider.departments[0].city}, ${provider.departments[0].state}`,
    };
  }

  return {
    clinicNameText: "Clinic name unavailable",
    clinicLocationText: "Clinic location unavailable",
  };
};

const getProviderPhotoSize = (isMobile: boolean, isConsolidatedFlowActive: boolean): number => {
  if (isConsolidatedFlowActive) {
    return isMobile ? 88 : 145.2;
  }

  return isMobile ? 51.2 : 84.48;
};

interface ProviderCardProps {
  isMobile: boolean;
  isMobileAvailability?: boolean;
  isCompactAvailability?: boolean;
  isStackedAvailability?: boolean;
  provider: Provider;
  providerPhoto?: ProviderPhotoData;
  providerPhotoIsLoading?: boolean;
  availability?: ProviderAvailabilityCardData;
  onSelectAvailabilitySlot?: (selection: AvailabilitySelection) => void;
  onToggleDateExpanded?: (providerId: string, isoDate: string) => void;
  onScheduleSelectedTime?: (providerId: string) => void;
  onPreviousAvailabilityDates?: (providerId: string) => void;
  onNextAvailabilityDates?: (providerId: string) => void;
}

interface ProviderHeaderContentProps {
  isMobile: boolean;
  isStackedAvailability: boolean;
  isConsolidatedFlowActive: boolean;
  hasProviderPhoto: boolean;
  selectedProviderPhoto?: ProviderPhotoData;
  providerPhotoIsLoading: boolean;
  providers: Provider;
  clinicNameText: string;
  clinicLocationText: string;
  effectivePhotoSize: number;
  effectivePhotoSizePx: string;
  onProviderNameClick: () => void;
}

const getConsolidatedIconHolderCss = (
  isConsolidatedFlowActive: boolean,
  isMobile: boolean,
  hasProviderPhoto: boolean,
) => {
  if (!isConsolidatedFlowActive) {
    return undefined;
  }

  const dimension = isMobile ? "88px" : "145.2px";
  if (!hasProviderPhoto) {
    return {
      maxWidth: dimension,
      maxHeight: dimension,
      padding: "0",
    };
  }

  return {
    maxWidth: dimension,
    maxHeight: dimension,
    width: dimension,
    height: dimension,
    padding: "0",
  };
};

const renderProviderName = (
  providers: Provider,
  isConsolidatedFlowActive: boolean,
  isStackedAvailability: boolean,
): ReactNode => {
  if (!isConsolidatedFlowActive) {
    return [providers.fullName, providers.providerType].filter(Boolean).join(", ");
  }

  if (!providers.providerType) {
    return providers.fullName;
  }

  if (isStackedAvailability) {
    return [providers.fullName, providers.providerType].filter(Boolean).join(", ");
  }

  return (
    <Fragment>
      {providers.fullName},
      <br />
      {providers.providerType}
    </Fragment>
  );
};

const renderModalityBadges = (modalities: string[]): ReactElement[] => {
  return modalities.map((apptType) => {
    const lowerType = apptType.toLowerCase();
    const iconType = lowerType === "virtual" ? "computer" : "person";
    const modalityText = lowerType === "virtual" ? apptType : "In person";

    return (
      <Badge
        outline
        variant="warning"
        icon={{ icon: iconType, variant: "outlined" }}
        ariaText={apptType}
        key={apptType}
      >
        {modalityText}
      </Badge>
    );
  });
};

const renderProviderHeaderContent = ({
  isMobile,
  isStackedAvailability,
  isConsolidatedFlowActive,
  hasProviderPhoto,
  selectedProviderPhoto,
  providerPhotoIsLoading,
  providers,
  clinicNameText,
  clinicLocationText,
  effectivePhotoSize,
  effectivePhotoSizePx,
  onProviderNameClick,
}: ProviderHeaderContentProps): ReactElement => {
  const consolidatedHeaderCss = isConsolidatedFlowActive
    ? { gap: "30px", width: "100%" }
    : undefined;
  const iconHolderCss = getConsolidatedIconHolderCss(
    isConsolidatedFlowActive,
    isMobile,
    hasProviderPhoto,
  );
  const profileHeaderInfoCss = isConsolidatedFlowActive
    ? { gap: isMobile ? "5px" : "10px", flex: "1 1 0", minWidth: 0 }
    : undefined;
  const modalityBadgesCss = isConsolidatedFlowActive
    ? {
        flexDirection: isStackedAvailability ? ("row" as const) : ("column" as const),
        flexWrap: isStackedAvailability ? ("wrap" as const) : ("nowrap" as const),
      }
    : undefined;

  return (
    <Fragment>
      <ProfileHeader data-testid="test-id-provider-card-profile-header" css={consolidatedHeaderCss}>
        {isConsolidatedFlowActive ? (
          <ProviderPhotoLink
            type="button"
            id={`obc-provider-card-id-${providers.providerId}-photo-link`}
            data-testid={`test-id-provider-card-${providers.providerId}-photo-link`}
            data-obc-label={`Provider Photo Link for provider ${providers.fullName}`}
            aria-label={`Open profile for ${providers.fullName}`}
            onClick={onProviderNameClick}
          >
            <IconHolder
              isMobile={isMobile}
              hasProviderPhoto={hasProviderPhoto}
              data-testid="test-id-provider-card-icon-holder"
              css={iconHolderCss}
            >
              <ProviderPhoto
                providerPhoto={selectedProviderPhoto}
                altText={`${providers.fullName} photo`}
                fallbackIconSize={effectivePhotoSize}
                fallbackIconStyle={{
                  width: effectivePhotoSizePx,
                  height: effectivePhotoSizePx,
                }}
                isLoading={providerPhotoIsLoading}
                loadingLabel={`Loading ${providers.fullName} photo`}
              />
            </IconHolder>
          </ProviderPhotoLink>
        ) : (
          <IconHolder
            isMobile={isMobile}
            hasProviderPhoto={hasProviderPhoto}
            data-testid="test-id-provider-card-icon-holder"
            css={iconHolderCss}
          >
            <ProviderPhoto
              providerPhoto={selectedProviderPhoto}
              altText={`${providers.fullName} photo`}
              fallbackIconSize={effectivePhotoSize}
              fallbackIconStyle={{
                width: effectivePhotoSizePx,
                height: effectivePhotoSizePx,
              }}
              isLoading={providerPhotoIsLoading}
              loadingLabel={`Loading ${providers.fullName} photo`}
            />
          </IconHolder>
        )}
        <ProfileHeaderInfo
          data-testid="test-id-provider-card-profile-header-info"
          isMobile={isMobile}
          css={profileHeaderInfoCss}
        >
          {isConsolidatedFlowActive ? (
            <ProviderNameLink
              type="button"
              id={`obc-provider-card-id-${providers.providerId}-name-link`}
              data-testid={`test-id-provider-card-${providers.providerId}-name-link`}
              data-obc-label={`Provider Name Link for provider ${providers.fullName}`}
              onClick={onProviderNameClick}
            >
              <ProviderNameText isMobile={isMobile}>
                {renderProviderName(providers, isConsolidatedFlowActive, isStackedAvailability)}
              </ProviderNameText>
            </ProviderNameLink>
          ) : (
            <ProviderNameText isMobile={isMobile}>
              {renderProviderName(providers, isConsolidatedFlowActive, isStackedAvailability)}
            </ProviderNameText>
          )}
          <ProviderCardText isMobile={isMobile}>{providers.clinicianType}</ProviderCardText>
          {!isConsolidatedFlowActive && (
            <Fragment>
              <ProviderCardText isMobile={isMobile}>{clinicNameText}</ProviderCardText>
              <ProviderCardText isMobile={isMobile}>{clinicLocationText}</ProviderCardText>
            </Fragment>
          )}
          <ModalityBadgesWrapper css={modalityBadgesCss}>
            {renderModalityBadges(providers.modality)}
          </ModalityBadgesWrapper>
          {isConsolidatedFlowActive && isStackedAvailability && !isMobile && (
            <ProviderMetaSection
              isInlineWithHeader={true}
              data-testid={`test-id-provider-card-${providers.providerId}-meta`}
            >
              <ProviderCardText isMobile={isMobile}>{clinicNameText}</ProviderCardText>
              <ProviderCardText isMobile={isMobile}>{clinicLocationText}</ProviderCardText>
            </ProviderMetaSection>
          )}
        </ProfileHeaderInfo>
      </ProfileHeader>
      {isConsolidatedFlowActive && (!isStackedAvailability || isMobile) && (
        <ProviderMetaSection data-testid={`test-id-provider-card-${providers.providerId}-meta`}>
          <ProviderCardText isMobile={isMobile}>{clinicNameText}</ProviderCardText>
          <ProviderCardText isMobile={isMobile}>{clinicLocationText}</ProviderCardText>
        </ProviderMetaSection>
      )}
    </Fragment>
  );
};

interface ProviderBottomSectionContentProps {
  isMobile: boolean;
  isConsolidatedFlowActive: boolean;
  specialitiesAndModalities: string[];
  specialitiesSet: Set<string>;
  visibleBadges: string[];
  remainingCount: number;
  containerRef: { current: HTMLDivElement | null };
  badgeRefs: { current: (HTMLDivElement | null)[] };
  providers: Provider;
  loading: boolean;
  availability?: ProviderAvailabilityCardData;
  onScheduleSelectedTime?: (providerId: string) => void;
  handleClick: () => Promise<void>;
}

const renderProviderBottomSectionContent = ({
  isMobile,
  isConsolidatedFlowActive,
  specialitiesAndModalities,
  specialitiesSet,
  visibleBadges,
  remainingCount,
  containerRef,
  badgeRefs,
  providers,
  loading,
  availability,
  onScheduleSelectedTime,
  handleClick,
}: ProviderBottomSectionContentProps): ReactElement => {
  return (
    <ProviderCardBottomSection
      isMobile={isMobile}
      data-testid="test-id-provider-card-specialities-modality-and-action-holder"
      css={
        isConsolidatedFlowActive
          ? {
              marginTop: isMobile ? "6px" : "8px",
              justifyContent: isMobile ? "flex-start" : "space-between",
              gap: isMobile ? "25px" : "20px",
              [`@media ${MEDIA_QUERIES.consolidatedSchedulingMax}`]: {
                flexDirection: "column",
                alignItems: "stretch",
                gap: "20px",
              },
            }
          : {
              [`@media (max-width: ${ABYSS_BREAKPOINTS.md}px)`]: {
                flexDirection: "column",
                alignItems: "stretch",
              },
            }
      }
    >
      <SpecialitiesAndModalities
        isMobile={isMobile}
        data-testid="test-id-provider-card-specialities-modality-holder"
        css={
          isConsolidatedFlowActive
            ? {
                gap: "10px",
                flex: "1 1 auto",
                width: "auto",
              }
            : undefined
        }
      >
        <SectionTitleText isMobile={isMobile}>Specialities and modalities</SectionTitleText>
        <BadgesContainer
          ref={containerRef}
          css={isConsolidatedFlowActive ? { gap: "10px" } : undefined}
        >
          {specialitiesAndModalities.map((specialty, index) => {
            const matchesSpecialities = specialitiesSet.has(specialty.toLowerCase());
            return (
              <div
                key={specialty}
                ref={(el) => {
                  badgeRefs.current[index] = el;
                }}
                style={{
                  visibility: index < visibleBadges.length ? "visible" : "hidden",
                  position: index < visibleBadges.length ? "relative" : "absolute",
                }}
              >
                <Badge
                  variant="neutral"
                  icon={matchesSpecialities ? "check" : undefined}
                  outline
                  css={{
                    "abyss-badge-root": {
                      borderRadius: "100px",
                      borderColor: "$app.border.badge.neutral",
                      maxWidth: isMobile ? "150px" : "160px",
                    },
                  }}
                >
                  {specialty}
                </Badge>
              </div>
            );
          })}
          {remainingCount > 0 &&
            (isMobile ? (
              <Badge
                variant="neutral"
                outline
                css={{
                  "abyss-badge-root": {
                    borderRadius: "100px",
                    borderColor: "$app.border.badge.neutral",
                  },
                }}
              >
                +{remainingCount}
              </Badge>
            ) : (
              <Badge
                variant="neutral"
                icon="add"
                outline
                css={{
                  "abyss-badge-root": {
                    borderRadius: "100px",
                    borderColor: "$app.border.badge.neutral",
                  },
                }}
              >
                {remainingCount}
              </Badge>
            ))}
        </BadgesContainer>
      </SpecialitiesAndModalities>
      {isConsolidatedFlowActive ? (
        <ScheduleButton
          isMobile={isMobile}
          variant="filled"
          id={`obc-provider-card-id-${providers.providerId}-schedule-selected-time-btn`}
          data-testid={`test-id-provider-card-${providers.providerId}-schedule-selected-time-button`}
          data-obc-label={`Schedule Selected Time Button for provider ${providers.fullName}`}
          onClick={() => {
            onScheduleSelectedTime!(providers.providerId);
          }}
          isDisabled={
            !availability!.selectedSlotKey ||
            availability!.isSubmittingSelection ||
            availability!.isSchedulingBlocked
          }
          css={{
            [`@media ${MEDIA_QUERIES.consolidatedSchedulingMax}`]: {
              width: "100%",
            },
          }}
        >
          Schedule selected time
        </ScheduleButton>
      ) : (
        <ScheduleButton
          isMobile={isMobile}
          id={`obc-provider-card-id-${providers.providerId}-schedule-now-btn`}
          data-testid={`test-id-provider-card-${providers.providerId}-schedule-now-button`}
          data-obc-label={`Schedule Now Button for provider ${providers.fullName}`}
          onClick={handleClick}
          isDisabled={loading}
          css={{
            [`@media (max-width: ${ABYSS_BREAKPOINTS.md}px)`]: { width: "100%" },
          }}
        >
          Schedule now
        </ScheduleButton>
      )}
    </ProviderCardBottomSection>
  );
};

interface ConsolidatedProviderCardProps {
  isMobile: boolean;
  isMobileAvailability: boolean;
  isCompactAvailability: boolean;
  isStackedAvailability: boolean;
  providers: Provider;
  availability: ProviderAvailabilityCardData;
  onSelectAvailabilitySlot: (selection: AvailabilitySelection) => void;
  onToggleDateExpanded: (providerId: string, isoDate: string) => void;
  onScheduleSelectedTime: (providerId: string) => void;
  onPreviousAvailabilityDates: (providerId: string) => void;
  onNextAvailabilityDates: (providerId: string) => void;
  providerHeaderContent: ReactElement;
  providerBottomSectionContent: ReactElement;
}

const renderConsolidatedProviderCard = ({
  isMobile,
  isMobileAvailability,
  isCompactAvailability,
  isStackedAvailability,
  providers,
  availability,
  onSelectAvailabilitySlot,
  onToggleDateExpanded,
  onScheduleSelectedTime,
  onPreviousAvailabilityDates,
  onNextAvailabilityDates,
  providerHeaderContent,
  providerBottomSectionContent,
}: ConsolidatedProviderCardProps): ReactElement => {
  return (
    <Card data-testid="test-id-provider-card" data-provider-id={providers.providerId}>
      <CardWrapper
        isMobile={isMobile}
        css={{
          gap: "25px",
          padding: isMobile ? "5px" : "10px",
        }}
      >
        <AvailabilityLayout isMobile={isMobile} isStacked={isStackedAvailability}>
          <LeftColumn isMobile={isMobile}>{providerHeaderContent}</LeftColumn>
          <RightColumn>
            <ProviderAvailabilityPanel
              isCompact={isCompactAvailability}
              isMobile={isMobileAvailability}
              providerId={providers.providerId}
              providerName={providers.fullName}
              visibleIsoDates={availability.visibleIsoDates}
              availableDateSet={availability.availableDateSet}
              slotsByIsoDate={availability.slotsByIsoDate}
              expandedDateSet={availability.expandedDateSet}
              isLoadingDates={availability.isLoadingDates}
              isLoadingSlots={availability.isLoadingSlots}
              errorMessage={availability.errorMessage}
              selectedSlotKey={availability.selectedSlotKey}
              isSubmittingSelection={availability.isSubmittingSelection}
              isSchedulingBlocked={availability.isSchedulingBlocked}
              showFooter={false}
              canGoPreviousDates={availability.canGoPreviousDates}
              canGoNextDates={availability.canGoNextDates}
              onSelectSlot={onSelectAvailabilitySlot}
              onToggleDateExpanded={onToggleDateExpanded}
              onScheduleSelectedTime={onScheduleSelectedTime}
              onPreviousDates={onPreviousAvailabilityDates}
              onNextDates={onNextAvailabilityDates}
            />
          </RightColumn>
        </AvailabilityLayout>
        {providerBottomSectionContent}
      </CardWrapper>
    </Card>
  );
};

export const ProviderCard = ({
  isMobile,
  isMobileAvailability = false,
  isCompactAvailability = false,
  isStackedAvailability = false,
  provider: providers,
  providerPhoto,
  providerPhotoIsLoading = false,
  availability,
  onSelectAvailabilitySlot,
  onToggleDateExpanded,
  onScheduleSelectedTime,
  onPreviousAvailabilityDates,
  onNextAvailabilityDates,
}: ProviderCardProps) => {
  const { data, setData, markRouteAsComplete, clearAppointmentSelection } = useApplicationContext();
  const { FEATURE_SCHEDULING_CONSOLIDATION } = useFeatureFlags();
  const isSchedulingConsolidationEnabled = Boolean(FEATURE_SCHEDULING_CONSOLIDATION);
  const selectedProviderPhoto = providerPhoto || providers.providerPhoto;
  const hasProviderPhoto = Boolean(getProviderPhotoSrc(selectedProviderPhoto));
  const specialitiesSet = useMemo(() => {
    return new Set(
      data?.specialities?.map((s) => {
        return s.toLowerCase();
      }) ?? [],
    );
  }, [data?.specialities]);

  const specialitiesAndModalities: string[] = useMemo(() => {
    return buildSpecialitiesAndModalities(providers, specialitiesSet);
  }, [providers, specialitiesSet]);

  const [visibleBadges, setVisibleBadges] = useState<string[]>([]);
  const [remainingCount, setRemainingCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const badgeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navigate = useNavigateWithUTM();
  const { loading } = useProviderListContext();
  const { runExclusive } = useExclusiveAction("find-provider:schedule-now");

  useEffect(() => {
    const calculateBadges = () => {
      if (containerRef.current && badgeRefs.current.length > 0) {
        const nextBadgeState = calculateVisibleBadgeState({
          container: containerRef.current,
          badges: badgeRefs.current,
          allLabels: specialitiesAndModalities,
          isMobile,
        });

        setVisibleBadges(nextBadgeState.visibleBadges);
        setRemainingCount(nextBadgeState.remainingCount);
      }
    };

    // Use ResizeObserver to recalculate badges on container resize
    const resizeObserver = new ResizeObserver(calculateBadges);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup observer on unmount
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [specialitiesAndModalities, isMobile]);

  const handleClick = async () => {
    await runExclusive(async () => {
      // Store the selected provider details in session storage through context
      setData("selectedProvider", {
        ...providers,
        providerPhoto: getProviderPhotoMetadata(selectedProviderPhoto),
      });
      setData("providerSelectionSource", APP_ROUTES.FIND_PROVIDER);
      // clear appointmentDetails selected when we change the provider
      clearAppointmentSelection();
      // set /find-provider route step to completed
      markRouteAsComplete(APP_ROUTES.FIND_PROVIDER);
      // Navigate to /provider-profile
      navigate(APP_ROUTES.PROVIDER_PROFILE);
    });
  };

  const { clinicNameText, clinicLocationText } = getClinicTexts(providers);

  const isConsolidatedFlowActive =
    isSchedulingConsolidationEnabled &&
    Boolean(
      availability &&
        onSelectAvailabilitySlot &&
        onToggleDateExpanded &&
        onScheduleSelectedTime &&
        onPreviousAvailabilityDates &&
        onNextAvailabilityDates,
    );

  const effectivePhotoSize = getProviderPhotoSize(isMobile, isConsolidatedFlowActive);
  const effectivePhotoSizePx = `${effectivePhotoSize}px`;

  const providerHeaderContent = renderProviderHeaderContent({
    isMobile,
    isStackedAvailability,
    isConsolidatedFlowActive,
    hasProviderPhoto,
    selectedProviderPhoto,
    providerPhotoIsLoading,
    providers,
    clinicNameText,
    clinicLocationText,
    effectivePhotoSize,
    effectivePhotoSizePx,
    onProviderNameClick: handleClick,
  });

  const providerBottomSectionContent = renderProviderBottomSectionContent({
    isMobile,
    isConsolidatedFlowActive,
    specialitiesAndModalities,
    specialitiesSet,
    visibleBadges,
    remainingCount,
    containerRef,
    badgeRefs,
    providers,
    loading,
    availability,
    onScheduleSelectedTime,
    handleClick,
  });

  const providerDetailsContent = (
    <Fragment>
      {providerHeaderContent}
      {providerBottomSectionContent}
    </Fragment>
  );

  if (isConsolidatedFlowActive) {
    return renderConsolidatedProviderCard({
      isMobile,
      isMobileAvailability,
      isCompactAvailability,
      isStackedAvailability,
      providers,
      availability: availability!,
      onSelectAvailabilitySlot: onSelectAvailabilitySlot!,
      onToggleDateExpanded: onToggleDateExpanded!,
      onScheduleSelectedTime: onScheduleSelectedTime!,
      onPreviousAvailabilityDates: onPreviousAvailabilityDates!,
      onNextAvailabilityDates: onNextAvailabilityDates!,
      providerHeaderContent,
      providerBottomSectionContent,
    });
  }

  return (
    <Card data-testid="test-id-provider-card" data-provider-id={providers.providerId}>
      <CardWrapper isMobile={isMobile}>{providerDetailsContent}</CardWrapper>
    </Card>
  );
};
